import {
  EngineReportDetails,
  FuelOilPropertiesConsumptionVm,
} from '@navstation-portal/reporting/data-model/engine-report';
import { Units } from '@navstation-portal/shared/data-model/ui';
import { RangeLimits, TooltipSeverity, getRangeTooltip, isOutOfRange } from '../range-tooltip.utils';

const RANGE_LIMITS = {
  density: { min: 600, max: 1200 },
  lcv: { min: 37_000, max: 44_000 },
  sulphur: { min: 0, max: 10 },
  fuelOilPressure: { min: 0, max: 10 },
  fuelOilTemperature: { min: 10, max: 160 },
} as const;

export function buildFuelOilPropertiesConsumptionVm(
  details: EngineReportDetails | undefined,
  reportPeriod: number
): FuelOilPropertiesConsumptionVm {
  const densityAt15 = details?.densityAt15;
  const densityOutOfRange = isOutOfRange(densityAt15, RANGE_LIMITS.density);
  const densityTooltip = getRangeTooltip(
    'Density',
    RANGE_LIMITS.density,
    Units.kilogramsPerCubicMeter,
    TooltipSeverity.Error
  );

  const lcv = details?.lcv;
  const lcvOutOfRange = isOutOfRange(lcv, RANGE_LIMITS.lcv);
  const lcvTooltip = getRangeTooltip(
    'Low Calorific Value (LCV)',
    RANGE_LIMITS.lcv,
    Units.kilojoulesPerKilogram,
    TooltipSeverity.Error
  );

  const sulphurPercent = details?.sulphurPercent;
  const sulphurOutOfRange = isOutOfRange(sulphurPercent, RANGE_LIMITS.sulphur);
  const sulphurTooltip = getRangeTooltip(
    'Sulphur percentage',
    RANGE_LIMITS.sulphur,
    Units.percent,
    TooltipSeverity.Error
  );

  const fuelOilPressure = details?.fuelOilPressure;
  const fuelOilPressureOutOfRange = isOutOfRange(fuelOilPressure, RANGE_LIMITS.fuelOilPressure);
  const fuelOilPressureTooltip = getRangeTooltip(
    'Fuel Oil Pressure',
    RANGE_LIMITS.fuelOilPressure,
    Units.bar,
    TooltipSeverity.Warning
  );

  const fuelOilTemperature = details?.fuelOilTemperature;
  const fuelOilTemperatureOutOfRange = isOutOfRange(fuelOilTemperature, RANGE_LIMITS.fuelOilTemperature);
  const fuelOilTemperatureTooltip = getRangeTooltip(
    'Fuel Oil Temperature',
    RANGE_LIMITS.fuelOilTemperature,
    Units.degreesCelsius,
    TooltipSeverity.Warning
  );

  const fuelConsumption = details?.fuelConsumption;
  const effectivePower = details?.effectivePower;
  const fuelConsumptionMin = 150e-6 * (effectivePower ?? 0) * reportPeriod;
  const fuelConsumptionMax = 300e-6 * (effectivePower ?? 0) * reportPeriod;
  const fuelConsumptionOutOfRange =
    fuelConsumption != null &&
    effectivePower != null &&
    (fuelConsumption < fuelConsumptionMin || fuelConsumption > fuelConsumptionMax);
  const fuelConsumptionRange: RangeLimits = { min: fuelConsumptionMin, max: fuelConsumptionMax };
  const fuelConsumptionTooltip = getRangeTooltip(
    'Fuel Consumption in Period',
    fuelConsumptionRange,
    Units.metricTons,
    TooltipSeverity.Warning
  );

  return {
    densityOutOfRange,
    densityTooltip,
    lcvOutOfRange,
    lcvTooltip,
    sulphurOutOfRange,
    sulphurTooltip,
    fuelOilPressureOutOfRange,
    fuelOilPressureTooltip,
    fuelOilTemperatureOutOfRange,
    fuelOilTemperatureTooltip,
    fuelConsumptionOutOfRange,
    fuelConsumptionTooltip,
  };
}
